export type PasswordPolicyTarget =
  | 'vaultPassphrase'
  | 'guestVaultPassphrase'
  | 'infoSharePassword'
  | 'folderGuestPassword'
  | 'sedrGuestPassword';

export const PASSWORD_POLICY_TARGETS: PasswordPolicyTarget[] = [
  'vaultPassphrase',
  'guestVaultPassphrase',
  'infoSharePassword',
  'folderGuestPassword',
  'sedrGuestPassword',
];

export interface PasswordPolicy {
  id: string;
  name: string;
  appliesTo: PasswordPolicyTarget[];
  minLength: number;
  maxLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireDigit: boolean;
  requireSpecial: boolean;
  minCharacterClasses: number;
  bannedWords: string[];
  blockCommonPasswords: boolean;
  disallowEmailLocalPart: boolean;
  maxRepeatedChars: number | null;
  maxSequentialChars: number | null;
  maxAgeDays: number | null;
}

export type PasswordPolicyErrorCode =
  | 'too_short'
  | 'too_long'
  | 'missing_uppercase'
  | 'missing_lowercase'
  | 'missing_digit'
  | 'missing_special'
  | 'too_few_classes'
  | 'banned_word'
  | 'common_password'
  | 'contains_email'
  | 'repeated_chars'
  | 'sequential_chars';

export interface PasswordValidationResult {
  ok: boolean;
  errors: PasswordPolicyErrorCode[];
}

export interface ValidatePasswordOptions {
  email?: string | null;
}

/** Compact built-in common-password blocklist (lowercase). */
export const COMMON_PASSWORDS = new Set([
  'password', 'password1', 'password12', 'password123', 'passw0rd',
  '123456', '1234567', '12345678', '123456789', '1234567890',
  'qwerty', 'qwerty123', 'qwertyuiop', 'asdfgh', 'asdfghjkl',
  'abc123', 'abcd1234', 'letmein', 'welcome', 'welcome1',
  'admin', 'admin123', 'administrator', 'root', 'toor',
  'login', 'master', 'sunshine', 'princess', 'football',
  'baseball', 'iloveyou', 'monkey', 'dragon', 'shadow',
  'superman', 'batman', 'trustno1', 'hunter2', 'secret',
  'changeme', 'default', 'guest', 'test', 'test1234',
  'shareweb', 'shareweb123', 'passphrase', 'passphrase1',
  'winter', 'summer', 'spring', 'autumn', 'christmas',
  'michael', 'jennifer', 'jordan', 'harley', 'ranger',
  'soccer', 'hockey', 'whatever', 'freedom', 'princess1',
  'hello', 'hello123', 'access', 'access14', 'zaq12wsx',
  '1q2w3e4r', '1qaz2wsx', 'qazwsx', 'password!', 'p@ssw0rd',
  'p@ssword', 'pass@word1', 'welcome123', 'admin@123',
  'letmein1', 'monkey123', 'dragon123', 'master123',
  'starwars', 'startrek', 'pokemon', 'minecraft', 'fortnite',
  'lovely', 'loveme', 'fuckyou', 'fuckyou1', 'asshole',
  'charlie', 'robert', 'thomas', 'george', 'andrew',
  'daniel', 'joshua', 'matthew', 'anthony', 'william',
  'nicole', 'jessica', 'ashley', 'amanda', 'melissa',
  'computer', 'internet', 'android', 'iphone', 'samsung',
  'google', 'microsoft', 'windows', 'linux', 'ubuntu',
]);

export function defaultPolicyForTarget(target: PasswordPolicyTarget): PasswordPolicy {
  const base: PasswordPolicy = {
    id: `default:${target}`,
    name: `Default (${target})`,
    appliesTo: [target],
    minLength: 1,
    maxLength: 256,
    requireUppercase: false,
    requireLowercase: false,
    requireDigit: false,
    requireSpecial: false,
    minCharacterClasses: 0,
    bannedWords: [],
    blockCommonPasswords: false,
    disallowEmailLocalPart: false,
    maxRepeatedChars: null,
    maxSequentialChars: null,
    maxAgeDays: null,
  };

  if (target === 'vaultPassphrase' || target === 'guestVaultPassphrase') {
    return { ...base, minLength: 12 };
  }

  if (target === 'infoSharePassword') {
    return { ...base, minLength: 8 };
  }

  return base;
}

export function resolvePolicy(
  policies: readonly PasswordPolicy[] | null | undefined,
  target: PasswordPolicyTarget,
): PasswordPolicy {
  const match = (policies ?? []).find((policy) => policy.appliesTo.includes(target));
  return match ? normalizePolicy(match) : defaultPolicyForTarget(target);
}

export function normalizePolicy(policy: PasswordPolicy): PasswordPolicy {
  return {
    ...policy,
    appliesTo: [...policy.appliesTo],
    bannedWords: [...(policy.bannedWords ?? [])],
    maxRepeatedChars: policy.maxRepeatedChars ?? null,
    maxSequentialChars: policy.maxSequentialChars ?? null,
    maxAgeDays: policy.maxAgeDays ?? null,
  };
}

export function validatePassword(
  password: string,
  policy: PasswordPolicy,
  options: ValidatePasswordOptions = {},
): PasswordValidationResult {
  const errors: PasswordPolicyErrorCode[] = [];
  const value = password ?? '';

  if (value.length < policy.minLength) {
    errors.push('too_short');
  }

  if (value.length > policy.maxLength) {
    errors.push('too_long');
  }

  const hasUpper = /[A-Z]/.test(value);
  const hasLower = /[a-z]/.test(value);
  const hasDigit = /\d/.test(value);
  const hasSpecial = /[^A-Za-z0-9]/.test(value);

  if (policy.requireUppercase && !hasUpper) {
    errors.push('missing_uppercase');
  }
  if (policy.requireLowercase && !hasLower) {
    errors.push('missing_lowercase');
  }
  if (policy.requireDigit && !hasDigit) {
    errors.push('missing_digit');
  }
  if (policy.requireSpecial && !hasSpecial) {
    errors.push('missing_special');
  }

  const classCount = [hasUpper, hasLower, hasDigit, hasSpecial].filter(Boolean).length;
  if (policy.minCharacterClasses > 0 && classCount < policy.minCharacterClasses) {
    errors.push('too_few_classes');
  }

  const lower = value.toLowerCase();
  for (const word of policy.bannedWords) {
    const trimmed = word.trim().toLowerCase();
    if (trimmed.length > 0 && lower.includes(trimmed)) {
      errors.push('banned_word');
      break;
    }
  }

  if (policy.blockCommonPasswords && COMMON_PASSWORDS.has(lower)) {
    errors.push('common_password');
  }

  if (policy.disallowEmailLocalPart && options.email) {
    const local = emailLocalPart(options.email);
    if (local && local.length >= 3 && lower.includes(local)) {
      errors.push('contains_email');
    }
  }

  if (policy.maxRepeatedChars != null && hasRepeatedRun(value, policy.maxRepeatedChars)) {
    errors.push('repeated_chars');
  }

  if (policy.maxSequentialChars != null && hasSequentialRun(value, policy.maxSequentialChars)) {
    errors.push('sequential_chars');
  }

  return { ok: errors.length === 0, errors };
}

export function assertPasswordPolicy(
  password: string,
  policy: PasswordPolicy,
  options: ValidatePasswordOptions = {},
): void {
  const result = validatePassword(password, policy, options);
  if (!result.ok) {
    throw new Error(result.errors[0] ?? 'password_policy_failed');
  }
}

export function applyPolicyToInputs(
  inputs: Iterable<HTMLInputElement | null | undefined>,
  policy: PasswordPolicy,
): void {
  for (const input of inputs) {
    if (!input) {
      continue;
    }
    input.minLength = policy.minLength;
    input.maxLength = policy.maxLength;
  }
}

export function isPassphraseExpired(
  passphraseSetAt: string | Date | null | undefined,
  maxAgeDays: number | null | undefined,
  now: Date = new Date(),
): boolean {
  if (maxAgeDays == null || maxAgeDays <= 0 || !passphraseSetAt) {
    return false;
  }

  const setAt = passphraseSetAt instanceof Date ? passphraseSetAt : new Date(passphraseSetAt);
  if (Number.isNaN(setAt.getTime())) {
    return false;
  }

  const ageMs = now.getTime() - setAt.getTime();
  return ageMs > maxAgeDays * 24 * 60 * 60 * 1000;
}

export function findOverlappingTargets(policies: readonly PasswordPolicy[]): PasswordPolicyTarget[] {
  const seen = new Map<PasswordPolicyTarget, number>();
  const overlaps = new Set<PasswordPolicyTarget>();
  policies.forEach((policy, index) => {
    for (const target of policy.appliesTo) {
      if (seen.has(target)) {
        overlaps.add(target);
      } else {
        seen.set(target, index);
      }
    }
  });
  return [...overlaps];
}

function emailLocalPart(email: string): string | null {
  const normalized = email.trim().toLowerCase();
  const at = normalized.lastIndexOf('@');
  if (at <= 0) {
    return null;
  }
  return normalized.slice(0, at);
}

function hasRepeatedRun(value: string, maxAllowed: number): boolean {
  if (maxAllowed < 1 || value.length <= maxAllowed) {
    return false;
  }

  let run = 1;
  for (let i = 1; i < value.length; i += 1) {
    if (value[i] === value[i - 1]) {
      run += 1;
      if (run > maxAllowed) {
        return true;
      }
    } else {
      run = 1;
    }
  }
  return false;
}

function hasSequentialRun(value: string, maxAllowed: number): boolean {
  if (maxAllowed < 2 || value.length <= maxAllowed) {
    return false;
  }

  const lower = value.toLowerCase();
  let asc = 1;
  let desc = 1;

  for (let i = 1; i < lower.length; i += 1) {
    const prev = lower.charCodeAt(i - 1);
    const curr = lower.charCodeAt(i);
    const isAlphaOrDigit =
      (prev >= 48 && prev <= 57) || (prev >= 97 && prev <= 122);
    const sameClass =
      ((prev >= 48 && prev <= 57) && (curr >= 48 && curr <= 57))
      || ((prev >= 97 && prev <= 122) && (curr >= 97 && curr <= 122));

    if (isAlphaOrDigit && sameClass && curr === prev + 1) {
      asc += 1;
      desc = 1;
    } else if (isAlphaOrDigit && sameClass && curr === prev - 1) {
      desc += 1;
      asc = 1;
    } else {
      asc = 1;
      desc = 1;
    }

    if (asc > maxAllowed || desc > maxAllowed) {
      return true;
    }
  }

  return false;
}
